import React, { useCallback, useContext, useMemo } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import map from 'lodash/map';
import set from 'lodash/set';

import { AddButton } from 'src/presentation/components/pages/shared/EditorSectionLayout';
import { AlertWarning } from '@ad-scripts/presentation/components/common/alert';
import { ContainerForOptions } from 'src/presentation/components/pages/ivr/layouts/IvrLayout';
import FormContext from 'src/presentation/contexts/FormContext';
import Condition from './Condition';
import useOptionsGeneratedIds from 'src/presentation/components/pages/ivr/hooks/useOptionsGeneratedIds';
import Then from './Then/Then';
import {
  AddButtonWrapper,
  StyledColumn,
  StyledSection,
} from 'src/presentation/components/pages/sidekick/threads/components/Layout';
import { useSelector } from 'react-redux';
import {
  selectCurrentAnonModule,
  selectCurrentModule,
} from 'src/presentation/components/pages/sidekick/threads/actions/threadsUiSlice';
import { ThreadModuleContext } from '../../ThreadModuleContext';

type RuleProps = {
  attr: string;
  disabled: boolean;
};

const Rule = ({ attr, disabled }: RuleProps) => {
  const { isAnon: isAnonModule } = useContext(ThreadModuleContext);

  let module = useSelector(selectCurrentModule);
  const anonModule = useSelector(selectCurrentAnonModule);

  if (isAnonModule && anonModule) {
    module = anonModule;
  }

  const isFilterByRule = module.name === 'filterByRule' || module.name === 'filterData';

  const notChangeableFields = useMemo<string[]>(() => {
    if (isFilterByRule) {
      return (get(module, 'controlValues.data') || []).filter(key => !!key);
    }
    return [];
  }, [module, isFilterByRule]);

  const showCondition = !isFilterByRule || notChangeableFields.length;

  if (!showCondition) {
    return (
      <StyledColumn>
        <AlertWarning role="alert">Please select data to filter</AlertWarning>
      </StyledColumn>
    );
  }

  return (
    <StyledColumn>
      <StyledSection>
        <Conditions attr={`${attr}.conditions`} disabled={disabled} notChangeableFields={notChangeableFields} />
      </StyledSection>
      <StyledSection>
        <Then attr={`${attr}.then`} disabled={disabled} isFilterByRule={isFilterByRule} />
      </StyledSection>
    </StyledColumn>
  );
};

type IfRuleProps = {
  attr: string;
  disabled: boolean;
  notChangeableFields: string[];
};

const Conditions = ({ attr, disabled, notChangeableFields }: IfRuleProps) => {
  const { workingState, updateState, onBlur } = useContext(FormContext);
  const conditions = get(workingState, attr, []);
  const { addOptionId, optionsIds, removeOptionIdByIndex } = useOptionsGeneratedIds(conditions);

  const handleAddButtonClick = useCallback(() => {
    const conditions = cloneDeep([
      ...get(workingState, attr, []),
      { if: { comparator: '=', fields: [], rhs: null } },
    ]) as any;
    conditions[conditions.length - 2].join = 'and';

    const newState = set({}, attr, conditions);
    updateState(newState, { replaceArrayValues: true });
    onBlur && onBlur({ currentState: newState });
    addOptionId();
  }, [attr, addOptionId, onBlur, updateState, workingState]);

  const handleDelete = useCallback(
    index => {
      const newState: any = cloneDeep(get(workingState, attr, []).filter((_, idx) => idx !== index));

      // remove last join if it exists
      if (newState.length > 0 && newState[newState.length - 1].join) {
        delete newState[newState.length - 1].join;
      }

      updateState(set({}, attr, newState), { replaceArrayValues: true });
      removeOptionIdByIndex(index);

      onBlur && onBlur({ currentState: newState });
    },
    [attr, onBlur, workingState, removeOptionIdByIndex, updateState]
  );

  return (
    <ContainerForOptions
      addButtonLabel="Add Condition"
      onAddButtonClick={handleAddButtonClick}
      disabled={disabled}
      label=""
      addButtonRender={({ 'data-aid': dataAid, onAddButtonClick }) => (
        <AddButtonWrapper>
          <AddButton data-aid={dataAid} onClick={onAddButtonClick} text="Add Condition" />
        </AddButtonWrapper>
      )}
      context={map(conditions, (_, idx) => {
        return (
          <Condition
            id={optionsIds[idx]}
            disabled={disabled}
            attrList={attr}
            index={idx}
            key={optionsIds[idx]}
            attr={`${attr}[${idx}]`}
            removeOptionIdByIndex={handleDelete}
            notChangeableFields={notChangeableFields}
          />
        );
      })}
    ></ContainerForOptions>
  );
};

export default Rule;
